import React, {
	createContext,
	FC,
	ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState
} from 'react';

import { FullPageError } from 'src/common/components/errors/full-page-error/FullPageError';
import { Logger } from 'src/common/services/Logger';
import { PlatformConfiguration } from 'src/common/types/ConfigurationTypes';

import { initCoralogixRum } from '../../services/analytics/CoralogixRumService';
import { isLoggedIn } from '../../services/AuthenticationService';
import { getCentralConfig } from '../../services/central-configuration/CentralConfigurationService';
import { DevelopmentToggles, getSingleToggle } from '../../services/TogglesService';
import { useUserTenant } from '../userTenantProvider/UserTenantProvider';

import { CONFIGURATION_PROVIDER_LABELS } from './ConfigurationProvider.labels';

type TChatbotLinkConfig = { link: string; name: string };

export type TConfiguration = Record<string, Record<string, unknown>> & {
	GLOBAL?: {
		CONFIG?: {
			SLACK_CHATBOT?: TChatbotLinkConfig;
			MS_TEAMS_CHATBOT?: TChatbotLinkConfig;
		};
		TOGGLES?: {
			ENABLE_CHATBOT_LINK?: boolean;
		};
	};
	PLATFORM?: PlatformConfiguration;
};
export type TGetToggle = (
	toggleId: DevelopmentToggles,
	// useUserToggle: bc for auth0 use toggle support. Deafult is false
	// If true, will try to extract auth0 value toggle from the user's token
	// If false, will try to extract the value from the central configuration instead
	useUserToggle?: boolean
) => boolean;

interface IConfigurationContext {
	configuration: TConfiguration;
	getToggle: TGetToggle;
}

const ConfigurationContext = createContext<IConfigurationContext>(null);

interface ConfigurationProviderProps {
	children: ReactNode;
}

const ConfigurationProvider: FC<ConfigurationProviderProps> = ({ children }) => {
	const [configuration, setConfiguration] = useState<TConfiguration>(null);
	const [hasConfigurationError, setHasConfigurationError] = useState(false);
	const { tenant } = useUserTenant();
	const hasInitializedCoralogix = useRef(false);

	const normalizeBooleanToggleValue = (toggle: string | boolean): string | boolean => {
		if (toggle === 'true') {
			return true;
		}
		if (toggle === 'false') {
			return false;
		}
		return toggle;
	};

	const getToggle = useCallback(
		(toggleId: DevelopmentToggles, useUserToggle = false): boolean => {
			const localToggleValue = getSingleToggle(toggleId, useUserToggle);
			return useUserToggle
				? localToggleValue
				: localToggleValue ||
						!!normalizeBooleanToggleValue(configuration?.TOGGLES?.[toggleId] as string | boolean);
		},
		[configuration]
	);

	const initConfiguration = useCallback(async (): Promise<void> => {
		if (!configuration) {
			try {
				const formattedConfig: TConfiguration = await getCentralConfig(tenant.products);
				if (!formattedConfig) {
					throw new Error('Central configuration response is empty');
				}
				setConfiguration(formattedConfig);
				setHasConfigurationError(false);

				// Initialize Coralogix RUM after configuration is loaded
				if (!hasInitializedCoralogix.current) {
					hasInitializedCoralogix.current = true;
					initCoralogixRum();
				}
			} catch (error) {
				Logger.error('Failed to initialize platform configuration', error);
				setHasConfigurationError(true);
			}
		}
	}, [tenant, configuration]);

	useEffect(() => {
		if (tenant && isLoggedIn()) {
			initConfiguration();
		}
	}, [initConfiguration, tenant]);

	const value = useMemo(() => ({ configuration, getToggle }), [configuration, getToggle]);

	if (hasConfigurationError) {
		return <FullPageError title={CONFIGURATION_PROVIDER_LABELS.loadingErrorTitle} />;
	}

	return <ConfigurationContext.Provider value={value}>{children}</ConfigurationContext.Provider>;
};

const useConfiguration = (): IConfigurationContext => useContext(ConfigurationContext);

export { ConfigurationProvider, useConfiguration };
